var abGlyphs = window.abGlyphs || ((t) => (/[\u0590-\u08ff\u0900-\u0dff\ufb1d-\ufefc]/.test(t) ? t.split(/(\s+)/).filter(Boolean) : [...t])); // shared by the MOGRT scripts, so a page loading two of them still runs both; joined scripts (Arabic, Hindi) animate by word so letters stay connected
/*
 * MOGRT use cases, Text Wiggle tiles (sections/mogrt-uses.liquid, kind
 * 'wiggle'; styles in assets/mogrt-uses-wiggle.css). Every character of a
 * tile's text wiggles while the tile is in view: a few degrees of turn and
 * a little lift, wandering the way the template's wiggle does.
 *
 * A tile reads its settings from the section: data-speed (0 calm to 3
 * fast), data-correlation (0 to 100: how much the letters move together),
 * data-anchor (top, center or bottom: the spot each letter turns around)
 * and data-font (sans, serif, mono, rounded or a font name). Any of them
 * may hold several values separated by ">": the tile steps through them,
 * one beat each, and the control under the text follows. A hover (or a
 * tap on touch screens) replays from the first value. With reduced
 * motion, or before this runs, the tile stands still at its last values.
 *
 * Self-contained: assets/mogrt-uses.js leaves these tiles alone.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const wait = (ms, run) => new Promise((resolve) => setTimeout(resolve, ms)).then(() => run.alive);

  const BEAT = 2600; // ms on each value
  const RATES = [1.4, 2.4, 3.8, 5.6]; // new turns a second, speed 0 to 3
  const TURN = 9; // degrees at most
  const LIFT = 0.07; // em at most
  const ANCHORS = { top: 17, center: 50, bottom: 83 }; // % down the letter
  const FONTS = {
    sans: '',
    serif: 'Georgia, "Times New Roman", serif',
    mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    rounded: 'ui-rounded, "SF Pro Rounded", "Arial Rounded MT Bold", sans-serif',
  };

  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
  const steps = (value) => String(value || '').split('>').map((v) => v.trim()).filter(Boolean);
  const pick = (list, k, fallback) => (list.length ? list[Math.min(k, list.length - 1)] : fallback);
  const capital = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  // smooth value noise from -1 to 1: a fresh random value every whole x,
  // eased between
  const hash = (n) => {
    const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return (x - Math.floor(x)) * 2 - 1;
  };
  const noiseValues = new Map();
  const noise = (seed, x) => {
    const i = Math.floor(x);
    let values = noiseValues.get(seed);
    if (!values || values.i !== i) {
      values = { i, from: hash(seed + i * 7.31), to: hash(seed + (i + 1) * 7.31) };
      noiseValues.set(seed, values);
    }
    const f = x - i;
    const u = f * f * (3 - 2 * f);
    return values.from * (1 - u) + values.to * u;
  };

  // each character in a span of its own (spaces stay text)
  const split = (el) => {
    const chars = [];
    el.querySelectorAll('.mu-wiggle__line').forEach((line) => {
      const text = line.textContent;
      line.textContent = '';
      abGlyphs(text).forEach((ch) => {
        if (/\s/.test(ch)) return line.append(ch);
        const span = document.createElement('span');
        span.className = 'mu-wiggle__char';
        span.textContent = ch;
        line.append(span);
        chars.push(span);
      });
    });
    return chars;
  };

  const player = (el) => {
    const screen = el.closest('[data-mu-screen]') || el.parentElement;
    const chars = split(el);
    const control = screen.querySelector('[data-mu-wiggle-control]');
    const kind = control ? control.dataset.muWiggleControl : '';
    const knob = screen.querySelector('[data-mu-wiggle-track]');
    const readout = screen.querySelector('[data-mu-wiggle-value]');
    const list = {
      speed: steps(el.dataset.speed),
      correlation: steps(el.dataset.correlation),
      anchor: steps(el.dataset.anchor),
      font: steps(el.dataset.font),
    };
    const beats = Math.max(1, ...Object.values(list).map((l) => l.length));
    const seed = Math.random() * 100;
    const target = { rate: RATES[1], corr: 0.2, amount: 1 };
    const now = { rate: RATES[1], corr: 0.2, amount: 1, phase: 0 };

    // the settings of beat k, and the control showing it
    const apply = (k) => {
      const speed = clamp(Math.round(Number(pick(list.speed, k, 1)) || 0), 0, 3);
      const corr = clamp((Number(pick(list.correlation, k, 20)) || 0) / 100, 0, 1);
      const anchor = pick(list.anchor, k, 'center').toLowerCase();
      const font = pick(list.font, k, '');
      target.rate = RATES[speed];
      target.corr = corr;
      el.style.setProperty('--mu-pivot', `${ANCHORS[anchor] ?? 50}%`);
      el.style.fontFamily = font ? FONTS[font.toLowerCase()] ?? font : '';
      if (knob && kind === 'speed') knob.style.setProperty('--at', `${Math.round(15 + (speed * 70) / 3)}%`);
      if (knob && kind === 'correlation') knob.style.setProperty('--at', `${clamp(Math.round(corr * 100), 6, 94)}%`);
      if (readout && kind === 'anchor') readout.textContent = capital(anchor);
      if (readout && kind === 'font') readout.textContent = capital(font);
    };

    // jump straight to beat k: no gliding knob, pivot or speed
    const snap = (k) => {
      screen.classList.add('mu-wiggle-snap');
      apply(k);
      now.rate = target.rate;
      now.corr = target.corr;
      screen.getBoundingClientRect();
      requestAnimationFrame(() => screen.classList.remove('mu-wiggle-snap'));
    };

    // every character's turn and lift: its own wander mixed with one they
    // all share, as much as the correlation says
    const pose = () => {
      const c = now.corr;
      const keep = 1 / Math.hypot(1 - c, c);
      const turnAll = noise(seed + 91.3, now.phase);
      const liftAll = noise(seed + 53.1, now.phase * 0.9);
      chars.forEach((ch, i) => {
        const turn = ((1 - c) * noise(seed + i * 3.7, now.phase) + c * turnAll) * keep;
        const lift = ((1 - c) * noise(seed + i * 3.7 + 1.9, now.phase * 0.9) + c * liftAll) * keep;
        ch.style.transform = `translateY(${(lift * LIFT * now.amount).toFixed(3)}em) rotate(${(turn * TURN * now.amount).toFixed(2)}deg)`;
      });
    };

    return {
      rest() {
        snap(beats - 1);
        now.amount = 1;
        now.phase = 2.35;
        pose();
      },
      // the frame loop for one run: the letters straighten, then wiggle
      play(run) {
        snap(0);
        now.amount = 0;
        let last = performance.now();
        const tick = (time) => {
          if (!run.alive) return;
          const dt = Math.min(64, time - last);
          last = time;
          now.rate += (target.rate - now.rate) * (1 - Math.exp(-dt / 320));
          now.corr += (target.corr - now.corr) * (1 - Math.exp(-dt / 420));
          now.amount += (target.amount - now.amount) * (1 - Math.exp(-dt / 260));
          now.phase += (dt / 1000) * now.rate;
          pose();
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      async pass(run) {
        for (let k = 0; k < beats; k += 1) {
          if (k) apply(k);
          if (!(await wait(BEAT, run))) return false;
        }
        if (beats > 1) apply(0);
        return true;
      },
    };
  };

  const init = (el) => {
    if (el.hasAttribute('data-mu-wiggle-ready')) return;
    el.setAttribute('data-mu-wiggle-ready', '');
    const screen = el.closest('[data-mu-screen]') || el;
    const tile = el.closest('.mogrt-uses__tile') || screen;
    const wiggle = player(el);
    let run = { alive: false };

    const stop = () => {
      run.alive = false;
      wiggle.rest();
    };
    const start = async () => {
      run.alive = false;
      if (still.matches) return wiggle.rest();
      const mine = (run = { alive: true });
      wiggle.play(mine);
      while (mine.alive && (await wiggle.pass(mine)) !== false && mine.alive);
    };

    wiggle.rest();
    let visible = false;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => {
          visible = entry.isIntersecting;
          visible ? start() : stop();
        },
        { threshold: 0.55 }
      ).observe(screen);
    }
    if (pointer.matches) tile.addEventListener('pointerenter', () => visible && start());
    else tile.addEventListener('click', () => start());
  };

  const arm = (root) => root.querySelectorAll('[data-mu-wiggle]').forEach(init);
  arm(document);
  document.addEventListener('shopify:section:load', (event) => arm(event.target));
})();

